/**
 * Inside/outside *regions* for per-viewer overhead line-of-sight (ADR-0017).
 *
 * A player outside the bank must not see the overhead speech text or name labels
 * of players/NPCs inside the bank, and vice-versa, so inside and outside read as
 * separate spaces. Like roof-hiding (ADR-0003) this is a pure, per-viewer
 * *render-time* decision: the authoritative world the server broadcasts is
 * identical for everyone, and only what each browser draws differs.
 *
 * The region notion is deliberately the same geometry roof-hiding already keys
 * off: the connected roof *structures* from `./roofHiding`. The tiles under a
 * building's roof cap are its interior footprint, so a tile inside that footprint
 * is "inside" that building and a tile in no footprint is the open plaza. Reusing
 * the roof footprint means the line-of-sight boundary is exactly the bank wall
 * the roof and carve logic already use - no second, drifting notion of "inside".
 *
 * Imports no Pixi and reads no clock, so it is fully headless-testable.
 */

import { key, roofStructures, type RoofCandidate, type RoofStructure } from './roofHiding';

/** Region id for a tile that is inside no structure footprint: the open plaza. */
export const OUTSIDE = -1;

/**
 * Which structure's interior footprint contains `tile`, as an index into
 * `structures`, or {@link OUTSIDE} when the tile is in the open plaza. Pass the
 * structures from {@link roofStructures} so a caller drawing many entities a
 * frame computes the flood-fill once and reuses it for every region lookup.
 */
export function regionIndex<T extends RoofCandidate>(
  structures: readonly RoofStructure<T>[],
  tile: { x: number; y: number },
): number {
  const k = key(tile.x, tile.y);
  for (let i = 0; i < structures.length; i++) {
    if (structures[i]!.footprint.has(k)) return i;
  }
  return OUTSIDE;
}

/**
 * Whether two tiles share a region: both inside the same building, or both in the
 * open plaza. This is the per-viewer line-of-sight test - an entity's overhead
 * name/speech renders for a viewer only when `sameRegion(statics, viewer, target)`.
 * Computes the structures itself; render hot paths should call {@link regionIndex}
 * against a once-per-frame {@link roofStructures} instead.
 */
export function sameRegion<T extends RoofCandidate>(
  statics: readonly T[],
  a: { x: number; y: number },
  b: { x: number; y: number },
): boolean {
  const structures = roofStructures(statics);
  return regionIndex(structures, a) === regionIndex(structures, b);
}
